/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * Marketing constraints:
 * - Use contractor language (leads, callbacks, bids, follow-up, jobs).
 * - Avoid promising standalone workflows that are estimate-gated in product.
 * - Avoid unverified financial metric claims; keep wording to visible
 *   account activity unless a metric is explicitly confirmed.
 */
"use client";
import {
  FeaturePageTemplate,
  type FeaturePageConfig,
} from "@/components/landing/feature-page-template";
import {
  Users,
  BarChart3,
  MapPin,
  Briefcase,
  FileText,
  Eye,
  Calculator,
  DollarSign,
  MessageSquare,
} from "lucide-react";

const config: FeaturePageConfig = {
  badge: "Client Management",
  title: "Stop losing deals",
  titleHighlight: "between calls and callbacks.",
  description:
    "Keep every lead, callback, site visit, estimate, and follow-up in one place so your team always knows who to contact next and what has already been promised.",

  featureGrid: {
    title: "Everything in client management",
    items: [
      {
        icon: Users,
        title: "Lead and Client Profiles",
        description:
          "Keep contact details, company info, and call history in one shared record.",
      },
      {
        icon: BarChart3,
        title: "Pipeline View From Your Estimates",
        description:
          "See lead and proposal progress from first inquiry to signed work inside your clients and estimates workflow.",
      },
      {
        icon: MapPin,
        title: "Jobsite Records",
        description:
          "Track multiple jobsites per client with access notes and project history.",
      },
      {
        icon: FileText,
        title: "Full Client Work History",
        description:
          "See proposals, estimates, projects, and invoices tied to each client record.",
      },
      {
        icon: Eye,
        title: "Account Health Snapshot",
        description:
          "See proposal activity, revenue context, and recent account movement so follow-up is driven by facts.",
      },
      {
        icon: Briefcase,
        title: "Next-Step Actions",
        description:
          "Start an estimate directly from the client record while context is fresh, then move into delivery once work is won.",
      },
    ],
  },

  workflow: {
    title: "How contractor sales teams run CRM day to day",
    description:
      "A practical rhythm for construction sales: capture leads, follow up on time, and keep delivery history tied to the same client.",
    steps: [
      {
        label: "Capture",
        title: "Capture the lead once",
        description:
          "Create one lead record with contact details and jobsite context so everyone starts from the same facts.",
        details: [
          "Lead and client profile basics in one record",
          "Multiple jobsites tied to one customer",
          "Clear ownership for callbacks and next actions",
          "Shared context for office and field teams",
        ],
      },
      {
        label: "Follow Up",
        title: "Run callbacks and site-visit follow-through",
        description:
          "Track who needs a callback, who needs a site visit, and who is waiting on a proposal so no serious lead is forgotten.",
        details: [
          "Lead and proposal stage progress from inquiry to signed work",
          "Simple stage updates during calls and reviews",
          "Follow-up ownership and timing clarity",
          "Better week-ahead sales planning",
        ],
      },
      {
        label: "Quote",
        title: "Build and send estimates from the same record",
        description:
          "Move from lead to estimate without re-entering client details, then track proposal and decision activity in the same timeline.",
        details: [
          "Estimate and proposal activity tied to the client",
          "Documents and notes stay attached to the same record",
          "Faster context for calls, emails, and site visits",
          "Cleaner handoff from sales to production",
        ],
      },
      {
        label: "Deliver",
        title: "Carry client context into execution and billing",
        description:
          "When work is sold, project and invoice history stay connected to the same client so your team has full account context.",
        details: [
          "Projects and services stay linked to the client",
          "Invoice and payment status visible from the same client record",
          "Account history is visible for repeat work",
          "Better retention and upsell opportunities",
        ],
      },
    ],
  },

  capabilities: [
    {
      icon: BarChart3,
      title: "Pipeline Visibility You Can Act On",
      description:
        "Give owners and managers a clear view of lead and proposal movement, plus upcoming decisions.",
      bullets: [
        "Opportunity status at a glance",
        "Win-rate trend visibility",
        "Open estimate visibility",
        "Outstanding balance awareness",
      ],
    },
    {
      icon: MapPin,
      title: "Jobsite Records For Multi-Location Clients",
      description:
        "Track multiple locations per client so site-specific history and logistics are easy to find before dispatch.",
      bullets: [
        "Multiple jobsites per client",
        "Address and access context",
        "Site-level project history",
        "Linked estimate and billing context",
      ],
    },
    {
      icon: FileText,
      title: "Connected Work History Across Sales and Delivery",
      description:
        "Keep sales and operations aligned by showing the full work history tied to each client.",
      bullets: [
        "Projects and services in one timeline",
        "Estimate and proposal progress visibility",
        "Invoice and payment status context",
        "Cleaner handoff from sales to production",
      ],
    },
    {
      icon: Briefcase,
      title: "Faster Next Steps During Calls",
      description:
        "Take action while context is fresh: start an estimate directly from the client record and keep the sales handoff moving.",
      bullets: [
        "Quick-start an estimate from the client record",
        "Cleaner lead-to-client transition",
        "Less back-and-forth navigation",
        "Shorter time from inquiry to proposal",
      ],
    },
  ],

  connections: {
    title: "How CRM connects to daily operations",
    items: [
      {
        icon: Calculator,
        flow: "Client → Estimates",
        description:
          "Build estimates with full client context and keep each revision tied to the right account.",
      },
      {
        icon: Briefcase,
        flow: "Client → Projects",
        description:
          "Once work is sold, the same client record carries into project execution without re-entry.",
      },
      {
        icon: DollarSign,
        flow: "Client → Invoices",
        description:
          "Billing and payment history stay connected to the customer record for cleaner collections.",
      },
      {
        icon: FileText,
        flow: "Client Timeline → Follow-Up Decisions",
        description:
          "Calls, notes, estimates, and proposal activity stay in one timeline so your next action is always clear.",
      },
    ],
  },

  relatedFeatures: [
    {
      label: "Proposals & Client Portal",
      href: "/features/proposals-portal",
      icon: FileText,
    },
    {
      label: "Estimates & Takeoffs",
      href: "/features/estimates",
      icon: Calculator,
    },
    {
      label: "Budgets & Invoices",
      href: "/features/budgets-invoices",
      icon: DollarSign,
    },
    {
      label: "Projects & Services",
      href: "/features/project-management",
      icon: Briefcase,
    },
    {
      label: "Communications",
      href: "/features/email-hub",
      icon: MessageSquare,
    },
  ],
};

export function CRMPageClient() {
  return <FeaturePageTemplate config={config} />;
}
